import { useEffect, useState } from 'react'

interface Metrics {
  innerHeight: number
  visualHeight: number
  visualOffsetTop: number
  visualPageTop: number
  visualScale: number
  clientHeight: number
  scrollY: number
  standalone: boolean
  keyboardInset: string
  safeTop: string
  safeBottom: string
  active: string
  chatPage: string
  header: string
  timeline: string
  composer: string
}

function rect(selector: string): string {
  const element = document.querySelector<HTMLElement>(selector)
  if (!element) return 'missing'
  const box = element.getBoundingClientRect()
  return `y=${Math.round(box.y)} h=${Math.round(box.height)} b=${Math.round(box.bottom)}`
}

function readMetrics(): Metrics {
  const viewport = window.visualViewport
  const styles = getComputedStyle(document.documentElement)
  const active = document.activeElement
  return {
    innerHeight: window.innerHeight,
    visualHeight: viewport ? Math.round(viewport.height) : -1,
    visualOffsetTop: viewport ? Math.round(viewport.offsetTop) : -1,
    visualPageTop: viewport ? Math.round(viewport.pageTop) : -1,
    visualScale: viewport ? Number(viewport.scale.toFixed(2)) : -1,
    clientHeight: document.documentElement.clientHeight,
    scrollY: Math.round(window.scrollY),
    standalone:
      window.matchMedia('(display-mode: standalone)').matches ||
      Boolean((navigator as Navigator & { standalone?: boolean }).standalone),
    keyboardInset: styles.getPropertyValue('--keyboard-inset').trim() || '0px',
    safeTop: styles.getPropertyValue('--safe-top').trim() || '0px',
    safeBottom: styles.getPropertyValue('--safe-bottom').trim() || '0px',
    active: active
      ? `${active.tagName.toLowerCase()}${active.id ? `#${active.id}` : ''}`
      : 'none',
    chatPage: rect('.chat-page'),
    header: rect('.chat-header'),
    timeline: rect('.chat-page__body'),
    composer: rect('.chat-composer-dock'),
  }
}

/**
 * Temporary keyboard diagnostics. Open a page with `?debug=keyboard` to show
 * it; it is never enabled automatically.
 */
export function KeyboardDebugOverlay() {
  const [metrics, setMetrics] = useState<Metrics | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const enabled = params.get('debug') === 'keyboard'
    if (!enabled) return

    let frame = 0
    let last = ''
    const update = () => {
      frame = 0
      // Serialize the metrics: iOS does not reliably fire viewport events
      // during the keyboard animation, so re-render only when a value really
      // changes while polling below.
      const next = JSON.stringify(readMetrics())
      if (next === last) return
      last = next
      setMetrics(JSON.parse(next) as Metrics)
    }
    const schedule = () => {
      if (frame !== 0) return
      frame = window.requestAnimationFrame(update)
    }
    // Poll: event listeners alone miss keyboard/pinch transitions on iOS.
    const poll = window.setInterval(schedule, 120)

    update()
    const viewport = window.visualViewport
    viewport?.addEventListener('resize', schedule)
    viewport?.addEventListener('scroll', schedule)
    window.addEventListener('resize', schedule)
    window.addEventListener('orientationchange', schedule)
    document.addEventListener('focusin', schedule)
    document.addEventListener('focusout', schedule)
    return () => {
      window.clearInterval(poll)
      if (frame !== 0) window.cancelAnimationFrame(frame)
      viewport?.removeEventListener('resize', schedule)
      viewport?.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('orientationchange', schedule)
      document.removeEventListener('focusin', schedule)
      document.removeEventListener('focusout', schedule)
    }
  }, [])

  if (!metrics) return null

  return (
    <aside
      data-testid="keyboard-debug"
      aria-hidden="true"
      style={{
        position: 'fixed',
        // Follow the visual viewport band the same way the chat page does, so
        // the metrics stay visible while iOS pans/zooms under the keyboard.
        top: 'calc(var(--safe-top) + 76px)',
        transform: 'translate(var(--chat-vv-offset-left, 0px), var(--chat-vv-offset, 0px))',
        left: 4,
        right: 4,
        zIndex: 10000,
        borderRadius: 8,
        background: 'rgb(9 42 58 / 0.9)',
        color: '#fff',
        padding: '6px 8px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: 9,
        lineHeight: 1.35,
        whiteSpace: 'pre-wrap',
        pointerEvents: 'none',
      }}
    >
      {[
        `standalone=${metrics.standalone} active=${metrics.active}`,
        `inner=${metrics.innerHeight} visual=${metrics.visualHeight} off=${metrics.visualOffsetTop} page=${metrics.visualPageTop} scale=${metrics.visualScale}`,
        `client=${metrics.clientHeight} scrollY=${metrics.scrollY} inset=${metrics.keyboardInset}`,
        `safe=${metrics.safeTop}/${metrics.safeBottom}`,
        `page ${metrics.chatPage}`,
        `head ${metrics.header}`,
        `body ${metrics.timeline}`,
        `comp ${metrics.composer}`,
      ].join('\n')}
    </aside>
  )
}
